import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Scheduler, Resource, View } from 'devextreme-react/scheduler';
import frMessages from "devextreme/localization/messages/fr.json";
import { locale, loadMessages } from "devextreme/localization";

import './CoursesCalendar.css';

const colors = [ '#CA94FA', '#9A83DE', '#9D9FF5', '#839CDE', '#94CCFA' ];

const CoursesCalendar = props => {
    const { courses, groupBy, defaultView, onlyAgenda } = props;
    const { t }    = useTranslation();

    useEffect(() => {
        loadMessages(frMessages);
        locale(navigator.language);
    }, [])

    const data = React.useMemo(() => {
        return courses?.map(course => ({
            startDate: course.start_at,
            endDate: course.stop_at,
            groupId: course.classroom?.group?.id,
            teacherId: course.classroom?.teacher?.id,
            subjectId: course.classroom?.subject?.id,
            text: (groupBy === 'groupId' ? [
                course.classroom?.subject?.name,
                course.classroom?.teacher?.firstname
            ] : [
                course.classroom?.group?.name,
                course.classroom?.subject?.name,
            ]).join(' - ')
        }));
    }, [courses, groupBy]);

    const groups = [groupBy];

    const groupsData = React.useMemo(() => {
        const groups = {};
        courses?.forEach(course => {
            const group = course.classroom?.group;
            groups[group?.id] = {
                id: group?.id,
                text: group?.name,
                min_age: group?.min_age
            };
        });
        return Object.values(groups)
            .sort((a, b) => a.min_age === b.min_age ? a.text.localeCompare(b.text) : a.min_age - b.min_age)
            .map((group, i) => ({
                ...group,
                color: colors[i]
            }));
    }, [courses]);

    const teachersData = React.useMemo(() => {
        const teachers = {};
        courses?.forEach(course => {
            const teacher = course.classroom?.teacher;
            teachers[teacher?.id] = {
                id: teacher.id,
                text: teacher.firstname,
            };
        });
        return Object.values(teachers)
            .sort((a, b) => a.text.localeCompare(b.text))
            .map((teacher, i) => ({
                ...teacher,
                color: colors[i]
            }));
    }, [courses]);

    const currentDate = React.useMemo(() => {
        let min = moment().add(1, 'year');;
        const today = moment().startOf('day');
        courses?.forEach(course => {
            if (course.start_at.isBefore(min) && course.start_at.isAfter(today))
                min = moment(course.start_at);
        });
        return min.toDate();
    }, [courses]);

    return (
        <div className="CoursesCalendar dx-viewport">
            { courses && (
                 <Scheduler
                    timeZone="Europe/Paris"
                    dataSource={data}
                    groups={groups}
                    firstDayOfWeek={1}
                    allDayPanelMode={'hidden'}
                    defaultCurrentView={onlyAgenda ? 'agenda' : (defaultView || 'day') }
                    defaultCurrentDate={currentDate}
                    height={'calc(100vh - 250px)'}
                    startDayHour={9}
                    endDayHour={19}
                    useDropDownViewSwitcher={!onlyAgenda}
                >
                    {!onlyAgenda && (
                        <View
                            type='day'
                            name={t('planning.timeline_day')}
                            intervalCount={1}
                        />
                    )}

                    {!onlyAgenda && (
                        <View
                            type='timelineWeek'
                            name={t('planning.timeline_week')}
                        />
                    )}

                    {!onlyAgenda && (
                        <View
                            type='month'
                            name={t('planning.timeline_month')}
                            groupOrientation='vertical'
                            maxAppointmentsPerCell='unlimited'
                        />
                    )}

                    <View
                        type='agenda'
                        name={t('planning.agenda')}
                        groupOrientation='vertical'
                    />

                    {(groupBy === 'teacherId') && (
                    <Resource
                        fieldExpr="teacherId"
                        allowMultiple={false}
                        dataSource={teachersData}
                        label={t('planning.teacher')}
                        useColorAsDefault={true}
                    />
                    )}

                    {(groupBy === 'groupId') && (
                    <Resource
                        fieldExpr="groupId"
                        allowMultiple={false}
                        dataSource={groupsData}
                        label={t('planning.group')}
                        useColorAsDefault={true}
                    />
                    )}
                </Scheduler>
            )}
        </div>
    );
}

export default CoursesCalendar;
